import React, { useState, useRef, useEffect } from 'react';
import './Sidebar.css';

function Sidebar({ domains, selectedDomain, onDomainSelect, responses }) {
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);
  const getDomainProgress = (domain) => {
    const allQuestions = domain.subcategories 
      ? domain.subcategories.flatMap(sub => sub.questions)
      : domain.questions || [];
    const answeredCount = allQuestions.filter(q => responses[q.id] !== undefined).length;
    return `${answeredCount}/${allQuestions.length}`;
  };

  const getDomainProgressPercentage = (domain) => {
    const allQuestions = domain.subcategories 
      ? domain.subcategories.flatMap(sub => sub.questions)
      : domain.questions || [];
    const answeredCount = allQuestions.filter(q => responses[q.id] !== undefined).length;
    return allQuestions.length > 0 ? (answeredCount / allQuestions.length) * 100 : 0;
  };

  const isDomainComplete = (domain) => {
    const allQuestions = domain.subcategories 
      ? domain.subcategories.flatMap(sub => sub.questions)
      : domain.questions || [];
    return allQuestions.every(q => responses[q.id] !== undefined);
  };

  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = e.clientX;
      if (newWidth >= 200 && newWidth <= 500) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div 
      ref={sidebarRef}
      className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}
      style={{ width: isCollapsed ? '60px' : `${sidebarWidth}px` }}
    >
      <div className="sidebar-header">
        {!isCollapsed && <h2>Topics</h2>}
        <button className="collapse-btn" onClick={toggleCollapse} title={isCollapsed ? 'Expand' : 'Collapse'}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {isCollapsed ? (
              <polyline points="9 18 15 12 9 6" />
            ) : (
              <polyline points="15 18 9 12 15 6" />
            )}
          </svg>
        </button>
      </div>
      {!isCollapsed && (
        <nav className="sidebar-nav">
          {domains.map((domain) => (
            <button
              key={domain.id}
              className={`domain-item ${selectedDomain.id === domain.id ? 'active' : ''} ${isDomainComplete(domain) ? 'complete' : ''}`}
              onClick={() => onDomainSelect(domain)}
            >
              <div className="domain-info">
                <span className="domain-name">{domain.domain}</span>
                <div className="progress-container">
                  <span className="domain-progress">{getDomainProgress(domain)}</span>
                  <div className="progress-bar-wrapper">
                    <div 
                      className="progress-bar-fill"
                      style={{ width: `${getDomainProgressPercentage(domain)}%` }}
                    />
                  </div>
                </div>
              </div>
              {isDomainComplete(domain) && (
                <span className="check-icon">✓</span>
              )}
            </button>
          ))}
        </nav>
      )}
      {isCollapsed && (
        <div className="collapsed-domains">
          {domains.map((domain) => (
            <button
              key={domain.id}
              className={`domain-item-collapsed ${selectedDomain.id === domain.id ? 'active' : ''} ${isDomainComplete(domain) ? 'complete' : ''}`}
              onClick={() => onDomainSelect(domain)}
              title={domain.domain}
            >
              {domain.domain.charAt(0)}
              {isDomainComplete(domain) && <span className="check-dot">•</span>}
            </button>
          ))}
        </div>
      )}
      {!isCollapsed && (
        <div 
          className="resize-handle"
          onMouseDown={handleMouseDown}
        />
      )}
    </div>
  );
}

export default Sidebar;
